/**
 * NI-23 — the variance investigation card's pure helpers: the vocabulary
 * the server uses (comparisons, request states, item statuses) and the
 * one-line summaries of what each finding covers. Kept out of the card so a
 * test can call them without pulling the UI kit in.
 */
export const COMPARISON_LABEL = {
  budget: 'vs budget',
  underwriting: 'vs underwriting',
  forecast: 'at the current pace',
  plan: 'business plan',
};

export const SEV_CHIP = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  high: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  low: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  info: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
};

export const DIRECTION_CLASS = {
  favorable: 'text-emerald-600 dark:text-emerald-400',
  unfavorable: 'text-red-600 dark:text-red-400',
};

export const STATE_LABEL = {
  queued: 'Investigation requested — Northline is starting it.',
  running: 'Northline is investigating…',
  refused: 'The last investigation was not published',
  failed: 'The last investigation failed',
  skipped: 'The last investigation did not run',
};

export const ITEM_STATUS_LABEL = {
  new: 'Awaiting a decision',
  approved: 'Seen and agreed',
  edited: 'Edited',
  dismissed: 'Dismissed',
  resolved: 'Resolved',
  auto_resolved: 'Superseded — the picture changed',
  expired: 'Expired',
};

export const money = (v) => (v == null ? '—' : `${v < 0 ? '−' : ''}$${Math.abs(Number(v)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
export const moneyCents = (cents) => (cents == null ? null : money(cents / 100));
const pct = (v) => (v == null ? null : `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(Number(v)).toFixed(1)}%`);
const signed = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${money(v)}`);

/** What one covered entry says, in Northline's numbers: the label, the
 * actual against the plan, and the variance with its percent. A milestone
 * has no planned/actual pair, so it reads its note instead. */
export function coveredLine(e) {
  if (e.comparison === 'plan') {
    return `${e.label}${e.due ? `, due ${e.due}` : ''}${e.note ? ` — ${e.note}` : ''}`;
  }
  if (e.unit !== 'currency') {
    const v = e.variance == null ? '—' : `${e.variance > 0 ? '+' : ''}${Number(e.variance).toLocaleString()}`;
    return `${e.label}: ${e.actual ?? '—'} actual vs ${e.planned ?? '—'} planned, ${v}${e.variance_pct != null ? ` (${pct(e.variance_pct)})` : ''}`;
  }
  return `${e.label}: ${money(e.actual)} actual vs ${money(e.planned)} planned, ${signed(e.variance)}`
    + `${e.variance_pct != null ? ` (${pct(e.variance_pct)})` : ''}`;
}

/** The headline under the title: how many findings, how many unfavorable,
 * and the exposure Northline summed. */
export function verdictLine(verdict, findings) {
  if (!verdict) return '';
  const n = findings ? findings.length : 0;
  const parts = [`${n} finding${n === 1 ? '' : 's'}`,
    `${verdict.unfavorable} unfavorable variance${verdict.unfavorable === 1 ? '' : 's'}`];
  if (verdict.exposure_cents) parts.push(`${moneyCents(verdict.exposure_cents)} against plan`);
  return parts.join(' · ');
}
